import { IconButton, Button, CircularProgress } from "@mui/material";
import { FC, useState, useEffect } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from "@tanstack/react-query";

export const DeleteConfirm: FC<{
  title: string;
  message: string;
  actionIcon: React.ReactNode;
  componentDisplay?: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  actionColor?: "error" | "primary" | "success";
}> = ({ title, message, actionIcon, componentDisplay, onConfirm, onCancel, actionColor = "error" }) => {
  const [countdown, setCountdown] = useState(2);
  const [canConfirm, setCanConfirm] = useState(false);

  const { mutateAsync: deleteManyBookmarks, isPending: isDeletingManyBookmarks } = useMutation({
    mutationFn: async () => {
      await onConfirm();
    },
  });

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanConfirm(true);
    }
  }, [countdown]);

  return (
    <div className={`px-4 py-3 min-w-[280px] rounded-lg transition-all duration-300 ${
      isDeletingManyBookmarks ? 'bg-gray-300' : ''
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-base font-semibold text-brand-1 flex-1">
          {title}
        </div>
        <IconButton
          size="small"
          onClick={onCancel}
          className="ml-2 text-neutral-700 hover:bg-neutral-100"
          disabled={isDeletingManyBookmarks}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      
      <div className="text-sm text-neutral-900 mb-4 font-medium">
        {message}
      </div>
      {componentDisplay && (
        <div className="text-sm text-neutral-900 mb-4 font-medium">
          {componentDisplay}
        </div>
      )}
      {isDeletingManyBookmarks && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-[#7fa4ca] text-white rounded-lg animate-pulse">
          <CircularProgress size={18} sx={{ color: 'white' }} />
          <span className="text-sm font-medium">Just a moment, working on it...</span>
        </div>
      )}
      <div className="flex items-center justify-end gap-3">
        <Button
          size="small"
          variant="outlined"
          onClick={onCancel}
          className="text-[0.813rem] px-2"
          disabled={isDeletingManyBookmarks}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color={actionColor}
          onClick={() => deleteManyBookmarks()}
          disabled={!canConfirm || isDeletingManyBookmarks}
          startIcon={!isDeletingManyBookmarks ? actionIcon : null}
          className={`text-[0.813rem] px-2 transition-opacity duration-300 ease ${
            canConfirm ? 'opacity-100' : 'opacity-40'
          }`}
        >
          {isDeletingManyBookmarks ? 'Working on it...' : `Delete`}
        </Button>
      </div>
    </div>
  );
};
